import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";

const URL = "https://rajshamani.life/guides/how-to-start-a-podcast-india";
const TITLE = "How to Start a Podcast in India (2026) — Raj Shamani";
const DESC =
  "A 2026 guide to starting a podcast in India: gear, recording, distribution, guest sourcing, and monetization — from the Figuring Out team.";


export const Route = createFileRoute("/guides/how-to-start-a-podcast-india")({
  head: () => ({
    meta: [
      { key: "title", title: TITLE },
      { key: "description", name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: "https://rajshamani.life/og-image.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: "https://rajshamani.life/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to Start a Podcast in India",
          description: DESC,
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life" },
          step: [
            { "@type": "HowToStep", name: "Pick a niche", text: "Define a specific audience and topic before buying any gear — niche shows grow faster in India." },
            { "@type": "HowToStep", name: "Buy the right starter kit", text: "A Shure MV7, Rode PodMic, or Samson Q2U covers 90% of Indian podcasters under ₹20,000 per mic." },
            { "@type": "HowToStep", name: "Record and edit", text: "Use Riverside or Zencastr for remote, Adobe Audition or Descript for editing." },
            { "@type": "HowToStep", name: "Distribute everywhere", text: "Publish to Spotify, Apple Podcasts, JioSaavn, and YouTube — video-first wins in India." },
            { "@type": "HowToStep", name: "Book great guests", text: "Start with your network, then trade reach with peer shows before chasing big names." },
            { "@type": "HowToStep", name: "Monetize", text: "Stack YouTube revenue, brand integrations, live shows, and a paid community once you cross 10k loyal listeners." },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: GuidePage,
});

const FAQS: { q: string; a: string }[] = [
  {
    q: "How much does it cost to start a podcast in India in 2026?",
    a: "A serious starter setup runs ₹25,000–₹75,000: mic (₹9–22k), audio interface (₹10–20k), headphones (₹8–12k), basic acoustic treatment (₹5–10k). You do not need a ₹3 lakh studio to publish your first 10 episodes.",
  },
  {
    q: "Which is the best podcast platform in India — YouTube or Spotify?",
    a: "YouTube is the #1 podcast destination in India by a wide margin, especially for long-form and vernacular content. Publish full episodes on YouTube and syndicate audio to Spotify, Apple Podcasts, and JioSaavn as table stakes.",
  },
  {
    q: "How do Indian podcasters make money?",
    a: "The realistic stack is YouTube monetization first, then brand integrations (the largest line for most Indian podcasters), followed by live shows, paid communities, and owned products like books or courses.",
  },
  {
    q: "How long does it take to grow a podcast in India?",
    a: "With a weekly schedule, 3–5 clips per episode, and consistent guest booking, most niche Indian podcasts hit a meaningful audience in 12–18 months. Consistency beats production value in year one.",
  },
  {
    q: "Do I need to podcast in Hindi to grow in India?",
    a: "Not required, but vernacular hooks, titles, and clips routinely 3–5× engagement over English-only. At minimum, write Hindi (or your guest's primary language) captions and thumbnails.",
  },
];

function H2({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl md:text-3xl font-display text-gold mt-12 mb-4 scroll-mt-24">
      {children}
    </h2>
  );
}

function GuidePage() {
  return (
    <article className="container max-w-3xl py-12 md:py-16">
      <PageBreadcrumb current="How to Start a Podcast in India" />

      <header className="mt-6 mb-10">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          Guide · ~14 min read
        </p>
        <h1 className="text-4xl md:text-5xl font-display leading-tight">
          How to Start a Podcast in India
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">
          A founder-friendly playbook on equipment, recording, distribution, guests, and monetization —
          drawn from what's working at <strong className="text-foreground">Figuring Out</strong>, one of
          India's most-watched business podcasts.
        </p>
      </header>

      <nav aria-label="On this page" className="border border-border rounded-lg p-5 mb-12 bg-card/40">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">On this page</p>
        <ol className="space-y-1 text-sm list-decimal pl-5">
          <li><a className="hover:text-gold" href="#why-now">Why podcast in India, why now</a></li>
          <li><a className="hover:text-gold" href="#niche">Pick your niche &amp; format</a></li>
          <li><a className="hover:text-gold" href="#gear">Equipment that's actually worth it</a></li>
          <li><a className="hover:text-gold" href="#recording">Recording &amp; editing workflow</a></li>
          <li><a className="hover:text-gold" href="#distribution">Distribution stack</a></li>
          <li><a className="hover:text-gold" href="#guests">Sourcing &amp; booking guests</a></li>
          <li><a className="hover:text-gold" href="#growth">Growth &amp; clip strategy</a></li>
          <li><a className="hover:text-gold" href="#monetize">Monetization in India</a></li>
          <li><a className="hover:text-gold" href="#mistakes">Mistakes to avoid</a></li>
        </ol>
      </nav>

      <div className="prose prose-invert max-w-none prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
        <H2 id="why-now">1. Why podcast in India, why now</H2>
        <p>
          India crossed 200M+ monthly podcast listeners in 2025, and YouTube is the #1 podcast platform in
          the country — not Spotify. Vernacular consumption (Hindi, Tamil, Telugu, Marathi) is growing
          ~3× faster than English. That means a new Indian podcaster can build a six-figure audience in
          12–18 months on a budget under ₹2 lakh, something unimaginable five years ago.
        </p>

        <H2 id="niche">2. Pick your niche &amp; format</H2>
        <p>
          Don't start with "a podcast". Start with one specific listener and one repeatable format. Ask:
        </p>
        <ul>
          <li><strong>Who exactly is this for?</strong> "Indian D2C founders doing ₹1–10 Cr" beats "entrepreneurs".</li>
          <li><strong>What format compounds?</strong> Solo, co-host, interview, or narrative. Interviews
            scale fastest in India because guests bring their own audience.</li>
          <li><strong>How long?</strong> 30–60 min long-form for YouTube + 60–90 sec clips for Reels and Shorts.</li>
        </ul>

        <H2 id="gear">3. Equipment that's actually worth it</H2>
        <p>
          Most first-time podcasters massively overspend. A working starter kit for an Indian studio
          (2026 pricing):
        </p>
        <ul>
          <li><strong>Microphone:</strong> Samson Q2U (~₹9k), Rode PodMic (~₹13k), or Shure MV7 (~₹22k).</li>
          <li><strong>Audio interface:</strong> Focusrite Scarlett Solo or Rodecaster Duo if you're going pro.</li>
          <li><strong>Headphones:</strong> Audio-Technica ATH-M40x or Sony MDR-7506.</li>
          <li><strong>Camera (video-first):</strong> Sony ZV-E10 or any modern phone on a tripod — quality of light matters more than camera body.</li>
          <li><strong>Acoustic treatment:</strong> 4 foam panels + a thick rug beats a ₹2 lakh "studio" with bad acoustics.</li>
        </ul>

        <H2 id="recording">4. Recording &amp; editing workflow</H2>
        <p>
          For remote guests use <strong>Riverside.fm</strong> or <strong>Zencastr</strong> — they record
          local high-quality tracks per participant. For editing, Descript (transcript-based editing) is
          the fastest learning curve; Adobe Audition or Logic Pro for finer control. Always export at
          -16 LUFS for Spotify and -14 LUFS for YouTube.
        </p>

        <H2 id="distribution">5. Distribution stack</H2>
        <p>
          The Indian podcast distribution ladder in 2026 — ship to every channel from day one, then double
          down on what works:
        </p>
        <ol>
          <li><strong>YouTube</strong> — the #1 podcast destination in India. Upload full episodes + clips.</li>
          <li><strong>Spotify + Apple Podcasts</strong> — table stakes via a host like Spotify for Podcasters, Buzzsprout, or Hubhopper.</li>
          <li><strong>JioSaavn, Gaana, Amazon Music</strong> — meaningful for vernacular and tier-2/tier-3 reach.</li>
          <li><strong>Instagram Reels + YouTube Shorts + LinkedIn</strong> — where new listeners actually discover you.</li>
          <li><strong>WhatsApp + email newsletter</strong> — your owned channel for the loyal 10%.</li>
        </ol>

        <H2 id="guests">6. Sourcing &amp; booking guests</H2>
        <p>
          Guests are your acquisition channel. The order that works:
        </p>
        <ul>
          <li><strong>Episodes 1–10:</strong> Your direct network. Don't chase celebrities you've never met.</li>
          <li><strong>Episodes 10–40:</strong> Peer trades — go on shows your size, invite their hosts on yours.</li>
          <li><strong>Episodes 40+:</strong> Cold outreach with proof — send specific episode links, a
            one-line value prop, and a single proposed date. Most "no"s are actually "wrong time".</li>
        </ul>

        <H2 id="growth">7. Growth &amp; clip strategy</H2>
        <p>
          Long-form builds depth; short-form builds reach. The discipline that compounds:
        </p>
        <ul>
          <li>Cut <strong>3–5 vertical clips per episode</strong> (30–90 sec) with captions baked in.</li>
          <li>Post clips on Reels, Shorts, and LinkedIn on a fixed schedule for 90 days before judging.</li>
          <li>Write a <strong>2-line hook in Hindi or your guest's primary language</strong> — vernacular hooks routinely 3–5× English engagement in India.</li>
          <li>Reply to every comment in week one of an episode launch — the algorithm rewards it.</li>
        </ul>

        <H2 id="monetize">8. Monetization in India</H2>
        <p>
          A realistic Indian monetization stack, in the order it should turn on:
        </p>
        <ol>
          <li><strong>YouTube monetization</strong> — kicks in at 1,000 subs + 4,000 hours. ₹50–200 RPM is typical.</li>
          <li><strong>Brand integrations</strong> — the biggest line for Indian podcasters. Price by audience quality, not just downloads.</li>
          <li><strong>Live shows &amp; events</strong> — Mumbai, Bengaluru, Delhi tour stops with ticketed access.</li>
          <li><strong>Paid community / cohort</strong> — once you've crossed ~10k loyal listeners.</li>
          <li><strong>Own products</strong> — books, courses, or a venture you can talk about authentically (see Raj Shamani's <em>Build Don't Talk</em> and House of X).</li>
        </ol>

        <H2 id="mistakes">9. Mistakes to avoid</H2>
        <ul>
          <li>Launching with a ₹3 lakh studio before you've published 10 episodes.</li>
          <li>Audio-only in 2026 — you're leaving 70%+ of Indian discovery on the table.</li>
          <li>Inconsistent schedule. One episode a week, same day, for a year beats a "launch" with five episodes.</li>
          <li>Chasing big-name guests too early. They don't move the needle without an audience to receive them.</li>
          <li>No clip team. If you can only afford one hire, hire a clip editor before a producer.</li>
        </ul>
      </div>

      <section aria-labelledby="faq" className="mt-16">
        <h2 id="faq" className="text-2xl md:text-3xl font-display text-gold mb-6">
          Frequently asked questions
        </h2>
        <div className="divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-4">
              <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between gap-4">
                {f.q}
                <span className="text-gold transition-transform group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section aria-labelledby="related" className="mt-16">
        <h2 id="related" className="text-2xl md:text-3xl font-display text-gold mb-6">
          Keep reading
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2">
          <li>
            <Link to="/guides/d2c-brands-india" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Guide</p>
              <p className="font-medium">How to start a D2C brand in India</p>
              <p className="text-sm text-muted-foreground mt-1">Sourcing, distribution, and unit economics for Indian consumer brands.</p>
            </Link>
          </li>
          <li>
            <Link to="/podcast" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Podcast</p>
              <p className="font-medium">Figuring Out with Raj Shamani</p>
              <p className="text-sm text-muted-foreground mt-1">Browse every episode with full summaries and transcripts.</p>
            </Link>
          </li>
          <li>
            <Link to="/build-dont-talk" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Book</p>
              <p className="font-medium">Build, Don't Talk</p>
              <p className="text-sm text-muted-foreground mt-1">Raj Shamani's bestseller on execution over talk.</p>
            </Link>
          </li>
          <li>
            <Link to="/guest" className="block p-5 rounded-lg border border-border hover:border-gold transition-colors">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Pitch</p>
              <p className="font-medium">Be a guest on Figuring Out</p>
              <p className="text-sm text-muted-foreground mt-1">Founders and operators — pitch your episode.</p>
            </Link>
          </li>
        </ul>
      </section>
    </article>
  );
}


